import { useTranslation } from "react-i18next";
import { useState } from "react";
import { X } from "lucide-react";
import { Label, TextField } from "react-aria-components";
import type { Manual, ManualBodySection } from "@arc/contracts";
import type { AuthSession } from "../../../auth/keycloak.js";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { useMarkManual, type ManualMutationError } from "../useManualMutations.js";

// Sentinel key for the leading "clear" row (react-aria `Key` can't be an empty string cleanly) → maps back to no selection.
const NONE = "__none__";

/**
 * The report / mark panel (ADR-002 Phase 8) — a docked LEFT side panel opened from the viewer rail's Report icon (a
 * `confirmed` manual, caller has `manuals_manage`). It lives in the same swappable flyout region as the Contents tree
 * (activity-bar model), so reporting and browsing the outline share one column. Free text + optional section + step
 * (from the loaded body); it fires the shipped `markManual` transition (ADR-018): confirmed → marked. Option A — the
 * section/step are folded into the plain-text comment (no contract change). On a 409 stale it asks the user to reopen;
 * on success it refreshes the viewer and collapses the panel.
 */
/** The contract's `max(1500)` — the legacy's cap, restored now that the location no longer rides inside the text. */
const MAX_REPORT = 1500;

export function ReportManualPanel({
  session,
  manual,
  sections,
  currentStepId,
  onClose,
  onMarked,
}: {
  session: AuthSession | null;
  manual: Manual;
  sections: ManualBodySection[];
  /** Where the reader is standing, so the commonest report — "this step, the one I am looking at" — costs no
   *  dropdowns (ADR-041 D4). The legacy opens its window prefilled the same way. */
  currentStepId?: number | null;
  onClose: () => void;
  onMarked: () => void;
}) {
  const { t } = useTranslation();
  const mark = useMarkManual(session);
  const [text, setText] = useState("");
  const here = currentStepId != null ? sections.find((sec) => sec.steps.some((st) => st.id === currentStepId)) : undefined;
  const [sectionId, setSectionId] = useState<number | "">(here?.id ?? "");
  const [stepId, setStepId] = useState<number | "">(here && currentStepId != null ? currentStepId : "");
  const [stale, setStale] = useState(false);

  const section = sections.find((s) => s.id === sectionId);
  const steps = section?.steps ?? [];
  const err = mark.error as ManualMutationError | null;
  const errorMsg =
    err && err.code === "MARK_LOCATION_INVALID"
      ? // The section or step was deleted while this was open. The typed report survives — only the location is gone.
        t("viewer.thatSectionOrStepIsNo")
      : err && err.code !== "MANUAL_STATE_STALE"
        ? t("viewer.couldntSubmitTheReportPleaseTry")
        : null;
  const canSubmit = text.trim().length > 0 && !mark.isPending && !stale;

  const submit = () => {
    // The location travels as IDS (ADR-041). It used to be flattened into the text as "Reported in: …", which the
    // reader's `parseMarkComment` could not recover — so the section and step the reporter picked were captured,
    // stringified and then lost. Nothing is reserved out of the text budget any more, because nothing rides in it.
    mark.mutate(
      {
        id: manual.id,
        expectedFrom: manual.state,
        comment: text.trim().slice(0, MAX_REPORT),
        ...(sectionId !== "" ? { sectionId } : {}),
        ...(sectionId !== "" && stepId !== "" ? { stepId } : {}),
      },
      {
        onSuccess: () => {
          onMarked();
          onClose();
        },
        onError: (e) => {
          if ((e as ManualMutationError).code === "MANUAL_STATE_STALE") setStale(true);
        },
      },
    );
  };

  return (
    <section aria-label={t("viewer.reportAProblem")} className="border-border flex w-full flex-col border-r">
      <div className="border-border flex items-center justify-between gap-2 border-b px-4 py-2.5">
        <h2 className="text-sm font-semibold">{t("viewer.reportAProblem")}</h2>
        <Button variant="ghost" size="icon-sm" aria-label={t("viewer.close")} onPress={onClose} isDisabled={mark.isPending}>
          <X aria-hidden="true" />
        </Button>
      </div>

      <div className="flex flex-1 flex-col gap-4 overflow-auto p-4">
        <p className="text-muted-foreground text-sm">
          {t("viewer.flagThisManualForRevisionIt")}
        </p>

        <Select
          className="text-sm"
          placeholder="—"
          selectedKey={sectionId === "" ? null : sectionId}
          onSelectionChange={(key) => {
            setSectionId(key === NONE || key == null ? "" : Number(key));
            setStepId("");
          }}
        >
          <Label className="font-medium">{t("viewer.sectionOptional")}</Label>
          <SelectTrigger />
          <SelectContent>
            <SelectItem id={NONE}>—</SelectItem>
            {sections.map((s) => (
              <SelectItem key={s.id} id={s.id}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          className="text-sm"
          placeholder="—"
          isDisabled={!section}
          selectedKey={stepId === "" ? null : stepId}
          onSelectionChange={(key) => setStepId(key === NONE || key == null ? "" : Number(key))}
        >
          <Label className="font-medium">{t("viewer.stepOptional")}</Label>
          <SelectTrigger />
          <SelectContent>
            <SelectItem id={NONE}>—</SelectItem>
            {steps.map((s) => (
              <SelectItem key={s.id} id={s.id}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <TextField className="flex flex-col gap-1 text-sm" value={text} onChange={setText}>
          <Label className="font-medium">{t("viewer.whatsWrong")}</Label>
          <Textarea rows={8} maxLength={MAX_REPORT} placeholder={t("viewer.describeTheProblem")} />
        </TextField>

        {stale ? (
          <p className="text-destructive text-sm">{t("viewer.reportStale")}</p>
        ) : null}
        {errorMsg ? <p className="text-destructive text-sm">{errorMsg}</p> : null}
      </div>

      <div className="border-border flex justify-end gap-2 border-t px-4 py-3">
        <Button variant="ghost" onPress={onClose} isDisabled={mark.isPending}>
          {t("viewer.cancel")}
        </Button>
        <Button onPress={submit} isDisabled={!canSubmit}>
          {mark.isPending ? t("viewer.submitting") : t("other.report.reportText")}
        </Button>
      </div>
    </section>
  );
}
